/* ═══════════════════════════════════════════════════════════════════════════
   L'HORLOGE DE LA NAVETTE — source unique de vérité du combat du monde 2.

   Même dispositif que `heraut.cycle.ts`, et pour la même raison : le moteur
   n'a aucune notion de phase, la synchronisation des armes n'existe que parce
   que tous les `firstDelay` sont calculés depuis une origine commune et que
   tous les `every` valent exactement CYCLE.

   ── Ce que ce boss enseigne, et en quoi il diffère du Héraut ──
   Le Héraut apprend au joueur qu'il PILOTE le boss : il vient à lui, lentement,
   et se laisse traîner dans un coin. Un joueur qui a bien retenu la leçon finit
   le monde 1 en restant collé sous le boss.

   La Navette prend ce réflexe à revers. Elle ne vient jamais : elle TRAVERSE,
   d'un bord à l'autre, sans se soucier de personne, et s'ancre à l'extrémité
   qu'elle vient d'atteindre pour y lâcher sa salve. Rester sous elle revient à
   la suivre dans son mouvement, donc à finir coincé contre le bord avec elle au
   moment précis où elle tire.

   La bonne réponse est l'inverse du monde 1 : se déplacer À CONTRESENS, croiser
   le boss en cours de traversée, et attendre la salve à l'autre extrémité. C'est
   exactement ce que le Tisseur enseigne à petite échelle avec son trou qui
   glisse — le monde 2 est le monde du mouvement continu, et son boss en est
   l'examen.
   ═══════════════════════════════════════════════════════════════════════════ */

export const CYCLE = 8.4;

/** Âge auquel le combat commence. Couvre le télégraphe (2,2 s) et la descente
 *  jusqu'à la station : apparition vers Y = +700, station à Y = +345, à 180 u/s
 *  avec l'accélération d'entrée. */
export const COMBAT_START = 3.4;

/* ── Découpage du cycle ─────────────────────────────────────────────────────

     0,0 ─── 5,0   TRAVERSÉE
                   Le porteur parcourt le terrain d'un bord à l'autre, à
                   vitesse CONSTANTE. Le sens s'inverse à chaque cycle, si bien
                   qu'il fait un aller-retour complet en 16,8 s.
       1,0         fuseaux — deux anneaux lents, le volume
       3,0         noyau   — un grand éventail statique, le plafond

     5,0 ─── 5,9   ANCRAGE
                   Le porteur s'immobilise NET à l'extrémité qu'il vient
                   d'atteindre. 0,9 s pendant lesquelles rien ne part.

                   ⚠️ L'arrêt signifie DANGER, exactement comme chez le Héraut.
                   Réutiliser le même signal pour la même chose est délibéré :
                   un vocabulaire visuel qui change de sens d'un monde à l'autre
                   n'enseigne rien, il désapprend.

     5,9           SALVE — les deux peignes tirent en visée croisée depuis le
                   bord. C'est là que le joueur paie d'avoir suivi le boss.

     5,9 ─── 6,4   La salve s'écarte du porteur.

     6,4 ─── 8,4   PUNITION — 2,0 s sans aucune émission, porteur immobile et
                   collé à un bord, donc parfaitement lisible. La fenêtre exigée
                   par PLAN.md §6.6 (≥ 1,8 s).
   ────────────────────────────────────────────────────────────────────────── */

export const PHASE_FUSEAUX = 1.0;
export const PHASE_NOYAU = 3.0;
/** Fin de la traversée : le porteur se fige, télégraphe de la salve. */
export const PHASE_ANCRAGE = 5.0;
export const PHASE_SALVE = 5.9;
export const PHASE_PUNITION = 6.4;

/** `firstDelay` d'une arme qui doit tirer à la phase `p` de chaque cycle. */
export function firstDelayAt(p: number): number {
  return COMBAT_START + p;
}

/** Amplitude de la traversée, en unités.
 *
 *  ⚠️ Les peignes sont boulonnés à ±180 u et le demi-terrain vaut 450 : au-delà
 *  de 250, un peigne sortirait du cadre et tirerait depuis le hors-champ.
 *  `motionSystem` repositionne une partie sur son porteur puis passe — la marge
 *  de culling ne s'applique jamais à elle, donc rien ne rattraperait l'erreur. */
export const SWEEP = 250;

/** Vitesse de traversée qui en découle, en u/s. Elle n'est pas réglée : elle se
 *  DÉDUIT de l'amplitude et de la durée de traversée. Écrire les trois nombres
 *  séparément laisserait deux d'entre eux dériver sans que le troisième s'en
 *  aperçoive. */
export const TRAVERSE_SPEED = (2 * SWEEP) / PHASE_ANCRAGE;

/**
 * Abscisse du porteur, calculée depuis le temps de combat.
 *
 * ⚠️ Position CALCULÉE et non intégrée. Une intégration (`x += v * dt`)
 * accumulerait une dérive au fil des dizaines de cycles d'un combat, et surtout
 * elle rendrait la trajectoire dépendante de l'historique des pas de temps :
 * deux replays de la même graine finiraient par diverger. Ici, la position ne
 * dépend que de l'âge — donc elle est exacte, et le golden replay tient.
 */
export function carrierX(t: number): number {
  if (t <= 0) return 0;
  // Repli manuel dans [0, CYCLE) et index de cycle. `Math.floor` est dans la
  // liste blanche : arrondi exact, donc identique sur tous les moteurs JS.
  const k = Math.floor(t / CYCLE);
  const phase = t - k * CYCLE;
  // Fraction de traversée accomplie, bornée : au-delà de PHASE_ANCRAGE le
  // porteur est arrêté, et `u` reste à 1.
  const u = phase >= PHASE_ANCRAGE ? 1 : phase / PHASE_ANCRAGE;
  // Parité du cycle sans `%` : elle donne le SENS de la traversée.
  const even = k - Math.floor(k / 2) * 2 === 0;
  const from = even ? -SWEEP : SWEEP;
  return from + (even ? 1 : -1) * (2 * SWEEP) * u;
}

/* ── Garde-fous de conception ──────────────────────────────────────────────
   Le module échoue à l'IMPORT plutôt que de laisser partir un boss subtilement
   injouable. Même esprit que `validate()` du registre : une erreur de contenu
   doit être bruyante au chargement, jamais silencieuse en jeu. */
if (CYCLE - PHASE_PUNITION < 1.8) {
  throw new Error(
    `Navette : fenêtre de punition de ${(CYCLE - PHASE_PUNITION).toFixed(2)} s, ` +
      `minimum 1,80 s (PLAN.md §6.6).`,
  );
}
if (PHASE_SALVE - PHASE_ANCRAGE < 0.5) {
  throw new Error(
    `Navette : télégraphe de salve de ${(PHASE_SALVE - PHASE_ANCRAGE).toFixed(2)} s, ` +
      `trop court pour être lu.`,
  );
}
/* La traversée doit rester nettement plus lente que le joueur (480 u/s), sinon
   « croiser le boss à contresens » cesse d'être une option et la leçon du
   combat devient inapplicable. */
if (TRAVERSE_SPEED > 240) {
  throw new Error(
    `Navette : traversée à ${TRAVERSE_SPEED.toFixed(0)} u/s, trop rapide pour être croisée.`,
  );
}
